import { mapGetters, mapState } from "vuex";
import { MODES, rowStatus } from "./entryModes";

// Карточка «Отмена» под каждой кнопкой сохранения
export const CANCEL = "cancel";
export const CANCEL_CARD = {
  type: CANCEL,
  title: "Отмена",
  icon: "el-icon-close",
  danger: true,
};

const isTimeout = (error) =>
  error.code === "ECONNABORTED" ||
  (error.message && error.message.includes("timeout"));

// Состояние стора, которое этапы меняют через v-model и .sync: { имя: мутация }
function twoWay(map) {
  const computed = {};
  Object.keys(map).forEach((name) => {
    computed[name] = {
      get() {
        return this.$store.state.wasteEntry[name];
      },
      set(value) {
        this.$store.commit("wasteEntry/" + map[name], value);
      },
    };
  });
  return computed;
}

/*
  Общее для этапов внесения: состояние из стора, предупреждения, отмена и сохранение.
  Этап сообщает наверх событиями saved и cancel, таблица этапа - ref="table",
  диалог паспорта - ref="passport"
*/
export default {
  computed: {
    ...mapState("wasteEntry", [
      "page",
      "mode",
      "action",
      "busy",
      "showErrors",
      "optionsLoading",
    ]),
    ...mapGetters("wasteEntry", [
      "isIncoming",
      "entryTitle",
      "hasEntered",
      "anySent",
      "unitName",
      "modeFields",
      "actionFields",
    ]),
    ...twoWay({
      step: "setStep",
      dates: "setDates",
      amounts: "setAmounts",
    }),
    // Поля строк режима и действия { [date]: { ... } }
    modeValues: {
      get() {
        return this.valuesOf(this.mode);
      },
      set(values) {
        this.setValues(this.mode, values);
      },
    },
    actionValues: {
      get() {
        return this.valuesOf(this.action);
      },
      set(values) {
        this.setValues(this.action, values);
      },
    },
    // «Отмена» и справа «Сохранить» - того же вида, что карточки дальнейших действий
    saveGroups() {
      return [
        {
          items: [
            CANCEL_CARD,
            {
              type: "save",
              title: "Сохранить",
              icon: "el-icon-check",
              primary: true,
            },
          ],
        },
      ];
    },
  },

  methods: {
    // Одно действие за раз: пока выполняется, остальные кнопки не срабатывают
    async withBusy(key, task) {
      if (this.busy) return;
      this.$store.commit("wasteEntry/setBusy", key);
      try {
        await task();
      } finally {
        this.$store.commit("wasteEntry/setBusy", null);
      }
    },

    valuesOf(type) {
      return this.$store.state.wasteEntry.values[type] || {};
    },

    setValues(type, values) {
      this.$store.commit("wasteEntry/setValues", { type, values });
    },

    setShowErrors(value) {
      this.$store.commit("wasteEntry/setShowErrors", value);
    },

    warn(message) {
      this.$message.warning(message);
      return false;
    },

    // Предупредить и прокрутить таблицу этапа к первой строке с ошибкой
    warnInTable(message) {
      this.$nextTick(() => {
        const table = this.$refs.table;
        if (table) table.scrollToFirstError();
      });
      return this.warn(message);
    },

    // Методом, а не @passport="$refs.passport.open": такой путь Vue вычисляет при отрисовке, когда ref ещё нет
    openPassport(date) {
      this.$refs.passport.open(date);
    },

    onSaveCard(type, save) {
      return type === CANCEL ? this.cancel() : save();
    },

    // Отмена сбрасывает всё введённое: мастер пересоздаётся и начинается с выбора режима
    async cancel() {
      if (this.busy) return;
      if (this.hasEntered) {
        try {
          await this.confirm(
            "Внимание",
            "Введённые данные не сохранены и будут потеряны. Отменить внесение?",
            "warning",
            "Да",
            "Нет"
          );
        } catch {
          return;
        }
      }
      this.$emit("cancel");
    },

    rowsValid(fields, values, skippable) {
      return this.dates.every(
        (date) => rowStatus(fields, values[date], skippable).valid
      );
    },

    /*
      Передача контрагенту без паспорта в части строк: спросить, сохранять ли так.
      «Добавить паспорт» - прокрутка к кнопке «Заполнить» первой такой строки
    */
    async confirmPassports(dates, values) {
      const missing = dates.find((date) => !(values[date] || {}).passport);
      if (!missing) return true;
      try {
        await this.$confirm(
          "Вывоз отходов осуществляется без заполнения сопроводительного паспорта перевозки отхода. Продолжить?",
          "Внимание",
          {
            type: "warning",
            confirmButtonText: "Сохранить",
            cancelButtonText: "Добавить паспорт",
            distinguishCancelAndClose: true,
          }
        );
        return true;
      } catch (action) {
        if (action === "cancel") this.$refs.table.scrollToField(missing, "passport");
        return false;
      }
    },

    send(type, dates, values) {
      return this.$store.dispatch("wasteEntry/send", { type, dates, values });
    },

    /*
      Сначала режим по всем датам, затем действие (оно есть только после поступления)
      по датам, где его заполнили. Поля режима проверены на этапе дат, здесь - только действия
    */
    async save() {
      const { mode, action, modeValues, actionValues } = this;
      let actionDates = [];
      if (action) {
        if (!this.rowsValid(this.actionFields, actionValues, true)) {
          this.setShowErrors(true);
          return this.warnInTable("Заполните выделенные поля");
        }
        actionDates = this.dates.filter(
          (date) => !rowStatus(this.actionFields, actionValues[date], true).skip
        );
        if (!actionDates.length)
          return this.warn(
            `Не заполнено ни одной строки. Чтобы сохранить только «${MODES[mode].title}», вернитесь к этапу «${this.entryTitle}» (нажмите на него вверху) и выберите «Только сохранить».`
          );
      }

      // Передача контрагенту - режимом расхода или действием после поступления
      if (mode === "contractor" && !(await this.confirmPassports(this.dates, modeValues)))
        return;
      if (
        action === "contractor" &&
        !(await this.confirmPassports(actionDates, actionValues))
      )
        return;

      let stage = "mode";
      try {
        await this.send(mode, this.dates, modeValues);
        stage = "action";
        if (action) await this.send(action, actionDates, actionValues);
      } catch (error) {
        if (isTimeout(error)) {
          this.$notify({
            message:
              "Выполняется сохранение данных на сервере. Время ожидания до 3 минут." +
              (action && stage === "mode"
                ? ` «${MODES[action].title}» внесите отдельно после сохранения.`
                : ""),
            type: "warning",
            duration: 60000,
          });
          this.$emit("saved");
          return;
        }
        this.$message.error(
          action && stage === "action"
            ? `«${MODES[mode].title}» сохранено, а «${MODES[action].title}» - нет. Нажмите «Сохранить», чтобы повторить.`
            : "Не удалось сохранить данные"
        );
        return;
      }

      this.$notify({ title: "Успешно", message: "Данные внесены", type: "success" });
      this.$emit("saved");
    },
  },
};
